import React, { useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import base64url from 'base64url';
import {
    Container,
    Grid,
    Column,
    Card,
    Loader,
    Pictorials,
    Typography,
    Section,
} from 'components';
import * as help from 'helpers';
import { ERRORS, URLS } from 'config';
import { authStore } from 'stores';

import { Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { StyledPictorial, StyledLink } from 'components/StyledCommon';

const Page = ({ history }: RouteComponentProps) => {
    help.useHiddenNavigation();
    const submitting = help.useBoolean(true);
    const [error, setError] = useState('');
    const { return_to, error_code } = help.useQueryParams();

    help.useEffectOnlyOnce(() => {
        if (error_code === '404') {
            submitting.setFalse();
            return setError(i18n._(ERRORS.apple_not_registered));
        }

        if (error_code === '499') {
            submitting.setFalse();
            return setError(i18n._(ERRORS.apple_user_canceled));
        }

        authStore
            .appleSignIn()
            .then(() => {
                if (typeof return_to === 'string') {
                    // we know where to redirect user after success
                    history.replace(
                        // sometimes it happens that we get two question marks in the redirect
                        base64url.decode(return_to).replace('??', '?'),
                    );
                    return;
                } else {
                    // default to homepage
                    history.replace('/');
                    return;
                }
            })
            .catch(onError);
    });

    const onError = () => {
        submitting.setFalse();
        setError(i18n._(ERRORS.apple_unknown));
    };

    if (submitting.value) {
        return (
            <Section topMargin minHeight="100vh" textCenter>
                <Container>
                    <Grid alignCenter>
                        <Column cols={8} desktop={7} tablet={10}>
                            <Card>
                                <StyledPictorial
                                    color="thumb"
                                    icon={Pictorials.Songs}
                                />

                                <Typography variant="h5" spaceBottom="base">
                                    <Trans>Logging in</Trans>
                                </Typography>

                                <Typography variant="bodys" spaceBottom="large">
                                    <Trans>
                                        Please wait while we log you into
                                        Yousician using your Apple id
                                    </Trans>
                                </Typography>
                                <Loader absolute position="bottom" />
                            </Card>
                        </Column>
                    </Grid>
                </Container>
            </Section>
        );
    }

    return (
        <Section topMargin minHeight="100vh">
            <Container>
                <Grid alignCenter>
                    <Column cols={8} desktop={7} tablet={10}>
                        {error && (
                            <Card>
                                <StyledPictorial
                                    color="thumb"
                                    icon={Pictorials.Error}
                                />

                                <Typography variant="h5" spaceBottom="base">
                                    <Trans>There's been an error</Trans>
                                </Typography>

                                <Typography variant="bodys" spaceBottom="large">
                                    {error}
                                    <br />
                                    <Trans>
                                        Please, contact our{' '}
                                        <StyledLink
                                            $size="bodys"
                                            href={URLS.supportNew}
                                        >
                                            support team
                                        </StyledLink>{' '}
                                        if the problem persists.
                                    </Trans>
                                </Typography>
                            </Card>
                        )}
                    </Column>
                </Grid>
            </Container>
        </Section>
    );
};

export default withRouter(observer(Page));
